import { useEffect } from 'react'
import { UserData } from '../../GlobalHeader'
import {
    AnalyticsEventMap,
    BotChallengeEvent,
    CTASelectedEvent,
    ClickVideoEvent,
    CloseGuestWelcomeEvent,
    CompleteLoginEvent,
    CompleteSignupEvent,
    CompletedPuzzleEvent,
    ConfirmEmailEvent,
    CustomChallengeEvent,
    DailyBonusClickedEvent,
    DailyBonusCollectedEvent,
    DirectChallengeEvent,
    EmailActivatedEvent,
    EnterAutoPoolEvent,
    ExitAutoPoolEvent,
    FeedbackPopupClosedEvent,
    FeedbackPopupShownEvent,
    FeedbackSharedEvent,
    PvpEmoteMenuOpenedEvent,
    PvpEmoteSentEvent,
    FlipBoardPressedEvent,
    FtueEndDialogueEvent,
    FtuePuzzleCompletedEvent,
    FtuePuzzleStartedEvent,
    GameButtonPressedEvent,
    GameFinishedEvent,
    GameOfferResultEvent,
    GameOfferSentEvent,
    GameStartedEvent,
    GuestContinuedEvent,
    LoginDismissedEvent,
    LoginPromptShownEvent,
    LogoutEvent,
    MenuButtonClickedEvent,
    MissionClaimedEvent,
    MissionCompletedEvent,
    NextPuzzleEvent,
    OfferRematchEvent,
    OfferRematchResultEvent,
    OpenLoginEvent,
    OpenSignupEvent,
    PopupClosedEvent,
    PopupEvents,
    PopupShownEvent,
    PowerUpUsedEvent,
    RequestResendEvent,
    ResignEvent,
    RewardEvent,
    ShareClickedEvent,
    ShareLinkCopiedEvent,
    SignupReachSecondPageEvent,
    StartedPuzzleEvent,
    analyticsManager,
} from './AnalyticsManager'
import { getHighestUserGroupName } from './utils'

type Props = {
    env: string
    userData: UserData | undefined
}

export const AnalyticsLoggingServiceComponent = ({ env, userData }: Props) => {
    useEffect(() => {
        if (env === 'prod') return

        const userId = userData?.id
        const userName = userData?.user_name
        const userType = userId ? getHighestUserGroupName(userData.groups) : 'visitor'

        const eventHandlers: { [key in keyof AnalyticsEventMap]?: Function } = {
            botChallenge: (e: BotChallengeEvent) => {
                console.log('[AnalyticsLoggingService] botChallenge event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            completeLogin: (e: CompleteLoginEvent) => {
                console.log('[AnalyticsLoggingService] completeLogin event', e, {
                    env,
                    userId: e.userId,
                    userType,
                    userName: e.userName,
                })
            },
            completeSignup: (e: CompleteSignupEvent) => {
                console.log('[AnalyticsLoggingService] completeSignup event', e, {
                    env,
                    userId: e.userId,
                    userType,
                    userName: e.userName,
                })
            },
            openSignup: (e: OpenSignupEvent) => {
                console.log('[AnalyticsLoggingService] openSignup event', e, { env, userId, userType, userName })
            },
            clickVideo: (e: ClickVideoEvent) => {
                console.log('[AnalyticsLoggingService] clickVideo event', e, { env, userId, userType, userName })
            },
            logout: (e: LogoutEvent) => {
                console.log('[AnalyticsLoggingService] logout event', e, { env, userId, userType, userName })
            },
            gameOfferSent: (e: GameOfferSentEvent) => {
                console.log('[AnalyticsLoggingService] gameOfferSent event', e, { env, userId, userType, userName })
            },
            gameOfferResult: (e: GameOfferResultEvent) => {
                console.log('[AnalyticsLoggingService] gameOfferResult event', e, { env, userId, userType, userName })
            },
            openLogin: (e: OpenLoginEvent) => {
                console.log('[AnalyticsLoggingService] openLogin event', e, { env, userId, userType, userName })
            },
            exitAutoPool: (e: ExitAutoPoolEvent) => {
                console.log('[AnalyticsLoggingService] exitAutoPool event', e, { env, userId, userType, userName })
            },
            customChallenge: (e: CustomChallengeEvent) => {
                console.log('[AnalyticsLoggingService] customChallenge event', e, { env, userId, userType, userName })
            },
            offerRematch: (e: OfferRematchEvent) => {
                console.log('[AnalyticsLoggingService] offerRematch event', e, { env, userId, userType, userName })
            },
            resign: (e: ResignEvent) => {
                console.log('[AnalyticsLoggingService] resign event', e, { env, userId, userType, userName })
            },
            completedPuzzle: (e: CompletedPuzzleEvent) => {
                console.log(`[AnalyticsLoggingService] completedPuzzle event - ${e.origin}`, e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            gameFinished: (e: GameFinishedEvent) => {
                console.log('[AnalyticsLoggingService] gameFinished event', e, { env, userId, userType, userName })
            },
            enterAutoPool: (e: EnterAutoPoolEvent) => {
                console.log('[AnalyticsLoggingService] enterAutoPool event', e, { env, userId, userType, userName })
            },
            gameStarted: (e: GameStartedEvent) => {
                console.log('[AnalyticsLoggingService] gameStarted event', e, { env, userId, userType, userName })
            },
            reward: (e: RewardEvent) => {
                console.log('[AnalyticsLoggingService] reward event', e, { env, userId, userType, userName })
            },
            directChallenge: (e: DirectChallengeEvent) => {
                console.log('[AnalyticsLoggingService] directChallenge event', e, { env, userId, userType, userName })
            },
            offerRematchResult: (e: OfferRematchResultEvent) => {
                console.log('[AnalyticsLoggingService] offerRematchResult event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            emailActivated: (e: EmailActivatedEvent) => {
                console.log('[AnalyticsLoggingService] emailActivated event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            loginPromptShown: (e: LoginPromptShownEvent) => {
                console.log('[AnalyticsLoggingService] loginPromptShown event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            guestContinued: (e: GuestContinuedEvent) => {
                console.log('[AnalyticsLoggingService] guestContinued event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            loginDismissed: (e: LoginDismissedEvent) => {
                console.log('[AnalyticsLoggingService] loginDismissed event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            confirmEmail: (e: ConfirmEmailEvent) => {
                console.log('[AnalyticsLoggingService] confirmEmail event', e, { env, userId, userType, userName })
            },
            requestResend: (e: RequestResendEvent) => {
                console.log('[AnalyticsLoggingService] requestResend event', e, { env, userId, userType, userName })
            },
            closeGuestWelcome: (e: CloseGuestWelcomeEvent) => {
                console.log('[AnalyticsLoggingService] closeGuestWelcome event', e, { env, userId, userType, userName })
            },
            signupReachSecondPage: (e: SignupReachSecondPageEvent) => {
                console.log('[AnalyticsLoggingService] signupReachSecondPage event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            nextPuzzle: (e: NextPuzzleEvent) => {
                console.log('[AnalyticsLoggingService] nextPuzzle event', e, { env, userId, userType, userName })
            },
            missionCompleted: (e: MissionCompletedEvent) => {
                console.log('[AnalyticsLoggingService] missionCompleted event', e, { env, userId, userType, userName })
            },
            missionClaimed: (e: MissionClaimedEvent) => {
                console.log('[AnalyticsLoggingService] missionClaimed event', e, { env, userId, userType, userName })
            },
            ftuePuzzleStarted: (e: FtuePuzzleStartedEvent) => {
                console.log('[AnalyticsLoggingService] ftuePuzzleStarted event', e, { env, userId, userType, userName })
            },
            ftuePuzzleCompleted: (e: FtuePuzzleCompletedEvent) => {
                console.log('[AnalyticsLoggingService] ftuePuzzleCompleted event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            popupTriggered: (e: PopupEvents) => {
                console.log('[AnalyticsLoggingService] popupTriggered event', e, { env, userId, userType, userName })
            },
            popupShown: (e: PopupShownEvent) => {
                console.log('[AnalyticsLoggingService] popupShown event', e, { env, userId, userType, userName })
            },
            popupClosed: (e: PopupClosedEvent) => {
                console.log('[AnalyticsLoggingService] popupClosed event', e, { env, userId, userType, userName })
            },
            ctaSelected: (e: CTASelectedEvent) => {
                console.log('[AnalyticsLoggingService] ctaSelected event', e, { env, userId, userType, userName })
            },
            dailyBonusClicked: (e: DailyBonusClickedEvent) => {
                console.log('[AnalyticsLoggingService] dailyBonusClicked event', e, { env, userId, userType, userName })
            },
            dailyBonusCollected: (e: DailyBonusCollectedEvent) => {
                console.log('[AnalyticsLoggingService] dailyBonusCollected event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            powerUpUsed: (e: PowerUpUsedEvent) => {
                console.log('[AnalyticsLoggingService] powerUpUsed event', e, { env, userId, userType, userName })
            },
            ftueEndDialogue: (e: FtueEndDialogueEvent) => {
                console.log('[AnalyticsLoggingService] ftueEndDialogue event', e, { env, userId, userType, userName })
            },
            feedbackPopupShown: (e: FeedbackPopupShownEvent) => {
                console.log('[AnalyticsLoggingService] feedbackPopupShown event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            feedbackPopupClosed: (e: FeedbackPopupClosedEvent) => {
                console.log('[AnalyticsLoggingService] feedbackPopupClosed event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
            menuButtonClicked: (e: MenuButtonClickedEvent) => {
                console.log('[AnalyticsLoggingService] menuButtonClicked event', e, { env, userId, userType, userName })
            },
            startedPuzzle: (e: StartedPuzzleEvent) => {
                console.log('[AnalyticsLoggingService] startedPuzzle event', e, { env, userId, userType, userName })
            },
            gameButtonPressed: (e: GameButtonPressedEvent) => {
                console.log('[AnalyticsLoggingService] gameButtonPressed event', e, { env, userId, userType, userName })
            },
            flipBoardPressed: (e: FlipBoardPressedEvent) => {
                console.log('[AnalyticsLoggingService] flipBoardPressed event', e, { env, userId, userType, userName })
            },
            shareClicked: (e: ShareClickedEvent) => {
                console.log('[AnalyticsLoggingService] shareClicked event', e, { env, userId, userType, userName })
            },
            shareLinkCopied: (e: ShareLinkCopiedEvent) => {
                console.log('[AnalyticsLoggingService] shareLinkCopied event', e, { env, userId, userType, userName })
            },
            feedbackShared: (e: FeedbackSharedEvent) => {
                console.log('[AnalyticsLoggingService] feedbackShared event', e, { env, userId, userType, userName })
            },
            pvpEmoteSent: (e: PvpEmoteSentEvent) => {
                console.log('[AnalyticsLoggingService] pvpEmoteSent event', e, { env, userId, userType, userName })
            },
            pvpEmoteMenuOpened: (e: PvpEmoteMenuOpenedEvent) => {
                console.log('[AnalyticsLoggingService] pvpEmoteMenuOpened event', e, {
                    env,
                    userId,
                    userType,
                    userName,
                })
            },
        }

        console.log('[AnalyticsLoggingService] initialize analytics logging service')

        // Add listeners using the stored references
        Object.entries(eventHandlers).forEach(([event, handler]) => {
            analyticsManager.addEventListener(event as any, handler as any)
        })

        return () => {
            // Remove listeners using the stored references
            Object.entries(eventHandlers).forEach(([event, handler]) => {
                analyticsManager.removeEventListener(event as any, handler as any)
            })
        }
    }, [env, userData?.id, userData?.groups])

    return null
}
